/**
 * Learning editor model: the three screens, the draft the engineer builds and the preview of what would be learned.
 * Pure functions only (no React, no network), so every screen state is testable. Plan: Documentation/LEARNING_EDITOR_PLAN.md.
 * What a lesson must contain, and how a draft is cleaned, is in editorRules.js.
 */
import { FIX_KINDS, MAX_LIST_ITEMS, cleanDraft, firstUnmet, withBlankRow } from "./editorRules";

export const STEP = Object.freeze({ FORM: "form", PREVIEW: "preview", RESULT: "result" });
export const RESULT = Object.freeze({ LEARNED: "learned", REVIEW: "review", REFUSED: "refused" });
export const REFUSAL = Object.freeze({ LEARNING_OFF: "learning_off", ALREADY_TAUGHT: "already_taught", NO_DIAGNOSIS: "no_diagnosis" });
export const LIST = Object.freeze({ SYMPTOMS: "symptoms", STEPS: "steps" });

export const ACTION = Object.freeze({
  SET_ROOT_CAUSE: "set_root_cause",
  SET_FIX_KIND: "set_fix_kind",
  SET_FIX_TEXT: "set_fix_text",
  SET_LIST_ITEM: "set_list_item",
  ADD_LIST_ITEM: "add_list_item",
  REMOVE_LIST_ITEM: "remove_list_item",
  TO_PREVIEW: "to_preview",
  BACK: "back",
  SUBMIT_START: "submit_start",
  SHOW_RESULT: "show_result",
  SUBMIT_FAILED: "submit_failed",
  SUBMIT_HELD: "submit_held", // the save was not sent, or the server asked to wait: back on the preview, no error
});

// Position of a screen in the step bar (TEXT.steps): Teach, Preview, Done.
const STEP_INDEX = Object.freeze({ [STEP.FORM]: 0, [STEP.PREVIEW]: 1, [STEP.RESULT]: 2 });
export const stepIndexOf = (step) => STEP_INDEX[step] ?? 0;

/**
 * `session` is {category: {key, label}, symptoms: string[], steps: string[], rootCause?: string, fixKind?: string, fixText?: string}:
 * what the chat already knows, which the engineer edits. The root cause and the fix start empty unless the caller passed them.
 */
export function initialState(session = {}) {
  return {
    category: session.category || null,
    draft: {
      symptoms: withBlankRow(session.symptoms),
      steps: withBlankRow(session.steps),
      rootCause: String(session.rootCause || ""),
      fixKind: FIX_KINDS.includes(session.fixKind) ? session.fixKind : "",
      fixText: String(session.fixText || ""),
    },
    step: STEP.FORM,
    preview: null,
    result: null,
    saving: false,
    error: false,
  };
}

/** Whether the screen's forward button is allowed, and the first requirement still unmet (`hint`, shown beside the button). */
export function gate(state) {
  if (state.step === STEP.RESULT) return { ok: true, hint: null };
  const cleaned = state.step === STEP.PREVIEW ? state.preview.draft : cleanDraft(state.draft).draft;
  const unmet = firstUnmet(state.category, cleaned);
  return { ok: !unmet && !state.saving, hint: unmet ? unmet.key : null };
}

/** What the caller saves: the cleaned draft in the server's field names. */
export const payloadOf = (state) => ({
  category: state.category ? state.category.key : null,
  symptoms: state.preview.draft.symptoms,
  resolution_steps: state.preview.draft.steps,
  root_cause: state.preview.draft.rootCause,
  fix_kind: state.preview.draft.fixKind,
  fix_text: state.preview.draft.fixText,
});

const replaceAt = (list, index, value) => list.map((x, i) => (i === index ? value : x));
const setList = (state, list, items) => ({ ...state, draft: { ...state.draft, [list]: items } });
const isList = (list) => list === LIST.SYMPTOMS || list === LIST.STEPS;

export function reducer(state, action) {
  switch (action.type) {
    case ACTION.SET_ROOT_CAUSE:
      return { ...state, draft: { ...state.draft, rootCause: action.value } };
    case ACTION.SET_FIX_KIND:
      return FIX_KINDS.includes(action.value) ? { ...state, draft: { ...state.draft, fixKind: action.value } } : state;
    case ACTION.SET_FIX_TEXT:
      return { ...state, draft: { ...state.draft, fixText: action.value } };
    case ACTION.SET_LIST_ITEM:
      return isList(action.list) ? setList(state, action.list, replaceAt(state.draft[action.list], action.index, action.value)) : state;
    case ACTION.ADD_LIST_ITEM:
      return isList(action.list) && state.draft[action.list].length < MAX_LIST_ITEMS
        ? setList(state, action.list, [...state.draft[action.list], ""]) : state;
    case ACTION.REMOVE_LIST_ITEM: {
      if (!isList(action.list)) return state;
      return setList(state, action.list, withBlankRow(state.draft[action.list].filter((_, i) => i !== action.index)));
    }
    case ACTION.TO_PREVIEW:
      return state.step === STEP.FORM && gate(state).ok ? { ...state, step: STEP.PREVIEW, preview: cleanDraft(state.draft), error: false } : state;
    case ACTION.BACK:
      return state.step === STEP.PREVIEW ? { ...state, step: STEP.FORM, preview: null, result: null, error: false } : state;
    case ACTION.SUBMIT_START:
      return state.step === STEP.PREVIEW && !state.saving ? { ...state, saving: true, error: false } : state;
    case ACTION.SHOW_RESULT:
      return state.saving ? { ...state, step: STEP.RESULT, saving: false, result: { kind: action.kind, reason: action.reason || null } } : state;
    case ACTION.SUBMIT_FAILED:
      return state.saving ? { ...state, saving: false, error: true } : state;
    case ACTION.SUBMIT_HELD:
      return state.saving ? { ...state, saving: false, error: false } : state;
    default:
      return state;
  }
}
